import {
  DISPOSICIONES,
  type Disposicion,
  formatearRevision,
  indexacionPorDefecto,
  MANEJOS,
  type Manejo,
  NOMBRE_MANEJO,
  tieneCopiaFisica,
  ubicacionDigital,
} from "@gestiondoc/shared";
import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { queryClient, useTRPC } from "../api.ts";
import type { Detalle } from "../tipos.ts";
import { mensajeDe, textoDeMeses } from "../utiles.ts";
import { Modal } from "./ModalFirma.tsx";
import { Aviso, Boton, Campo, CLASE_ENTRADA, Dato, Pildora } from "./ui.tsx";

/**
 * Control de registros simplificado (acordado el 2026-09-11). Lo único que se pregunta de
 * entrada es dónde está el papel, y solo si hay papel. La copia digital es GestionDOC; la
 * indexación y el responsable salen prellenados y se cambian solo si hace falta. Aquí están los
 * campos del formulario, el modal para corregirlos y el resumen que muestra el documento.
 */

export type ValorControl = {
  ubicacion: string;
  indexacion: string;
  responsableAlmacenamiento: string;
  retencionMeses: number;
  disposicion: Disposicion;
};

export const CONTROL_VACIO: ValorControl = {
  ubicacion: "",
  indexacion: "",
  responsableAlmacenamiento: "",
  retencionMeses: 24,
  disposicion: "destruccion",
};

const RETENCIONES = [6, 12, 24, 36, 60, 120];

export function CamposControlRegistros({
  codigo,
  nombre,
  puestoPorDefecto,
  manejo,
  valor,
  onCambio,
  errores,
  sinBorrador = false,
}: {
  /** True = se corrige la vigente: no hay un envío a revisión más tarde donde pedir la ubicación. */
  sinBorrador?: boolean;
  /** Código del documento, o null si todavía no se asignó (formulario nuevo). */
  codigo: string | null;
  nombre: string;
  puestoPorDefecto: string;
  manejo: Manejo;
  valor: ValorControl;
  onCambio: (v: ValorControl) => void;
  errores: Record<string, string>;
}) {
  const poner = (cambios: Partial<ValorControl>) => onCambio({ ...valor, ...cambios });
  const indexacionSugerida = indexacionPorDefecto(nombre || "Registro");
  return (
    <div className="space-y-4">
      <div className="rounded-md bg-marino-claro px-3 py-2 text-[12.5px] text-marino">
        <span className="font-semibold">Copia digital:</span>{" "}
        {codigo ? ubicacionDigital(codigo) : "GestionDOC"} · la guarda el sistema con cada revisión.
      </div>

      {tieneCopiaFisica(manejo) && (
        <Campo
          etiqueta="Dónde se archiva la copia física"
          ayuda={
            sinBorrador
              ? "Hace falta para guardar: la revisión ya está vigente."
              : "Puedes guardar el borrador sin esto; se pide antes de enviar a revisión."
          }
          error={errores["registro.ubicacion"]}
        >
          <input
            value={valor.ubicacion}
            onChange={(e) => poner({ ubicacion: e.target.value })}
            placeholder="Oficina de Logística, archivero 2, carpeta azul"
            className={CLASE_ENTRADA}
          />
        </Campo>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Campo
          etiqueta="Tiempo de retención"
          ayuda="Te avisamos un mes antes de que se cumpla."
          error={errores["registro.retencionMeses"]}
        >
          <select
            value={valor.retencionMeses}
            onChange={(e) => poner({ retencionMeses: Number(e.target.value) })}
            className={CLASE_ENTRADA}
          >
            {RETENCIONES.map((m) => (
              <option key={m} value={m}>
                {textoDeMeses(m)}
              </option>
            ))}
          </select>
        </Campo>
        <Campo etiqueta="Disposición al vencer">
          <select
            value={valor.disposicion}
            onChange={(e) => poner({ disposicion: e.target.value as Disposicion })}
            className={CLASE_ENTRADA}
          >
            {DISPOSICIONES.map((d) => (
              <option key={d} value={d}>
                {d === "destruccion" ? "Destrucción" : "Archivo pasivo"}
              </option>
            ))}
          </select>
        </Campo>
      </div>

      <details className="group rounded-md border border-borde px-3 py-2">
        <summary className="cursor-pointer text-[12.5px] font-semibold text-marino">
          Indexación y responsable{" "}
          <span className="font-normal text-texto-3">
            · {valor.indexacion || indexacionSugerida} ·{" "}
            {valor.responsableAlmacenamiento || puestoPorDefecto}
          </span>
        </summary>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <Campo
            etiqueta="Forma de indexación"
            ayuda="Vacío: se usa la sugerida."
            error={errores["registro.indexacion"]}
          >
            <input
              value={valor.indexacion}
              onChange={(e) => poner({ indexacion: e.target.value })}
              placeholder={indexacionSugerida}
              className={CLASE_ENTRADA}
            />
          </Campo>
          <Campo
            etiqueta="Responsable de almacenamiento"
            ayuda="Vacío: quien lo carga."
            error={errores["registro.responsableAlmacenamiento"]}
          >
            <input
              value={valor.responsableAlmacenamiento}
              onChange={(e) => poner({ responsableAlmacenamiento: e.target.value })}
              placeholder={puestoPorDefecto}
              className={CLASE_ENTRADA}
            />
          </Campo>
        </div>
      </details>
    </div>
  );
}

/**
 * Completar o corregir el control de un registro mientras hay un borrador, o, con el permiso
 * temporal, el de la vigente importada (`corregir`).
 */
export function ModalEditarControl({
  revisionId,
  corregir = false,
  codigo,
  nombre,
  manejoActual,
  actual,
  onCerrar,
  onGuardado,
}: {
  /** El borrador, o la vigente si `corregir`. */
  revisionId: string;
  corregir?: boolean;
  codigo: string;
  nombre: string;
  manejoActual: Manejo;
  actual: {
    ubicacion: string | null;
    indexacion: string;
    responsableAlmacenamiento: string;
    retencionMeses: number;
    disposicion: string;
  };
  onCerrar: () => void;
  onGuardado: () => void;
}) {
  const trpc = useTRPC();
  const [manejo, setManejo] = useState<Manejo>(manejoActual);
  const [valor, setValor] = useState<ValorControl>({
    ubicacion: actual.ubicacion ?? "",
    indexacion: actual.indexacion,
    responsableAlmacenamiento: actual.responsableAlmacenamiento,
    retencionMeses: actual.retencionMeses,
    disposicion: actual.disposicion as Disposicion,
  });
  const [error, setError] = useState<string | null>(null);
  const alTerminar = {
    onSuccess: () => {
      queryClient.invalidateQueries();
      onGuardado();
    },
    onError: (e: unknown) => setError(mensajeDe(e)),
  };
  const editar = useMutation(trpc.documentos.editarControl.mutationOptions(alTerminar));
  const corregirlo = useMutation(trpc.documentos.corregirControl.mutationOptions(alTerminar));
  const guardar = corregir ? corregirlo : editar;

  function enviar(e: FormEvent) {
    e.preventDefault();
    setError(null);
    guardar.mutate({ revisionId, manejo, registro: valor });
  }

  return (
    <Modal titulo="Control de registros" onCerrar={onCerrar}>
      <form onSubmit={enviar} className="space-y-4">
        {corregir && (
          <Aviso tono="ambar">
            Tienes el permiso temporal: el cambio rige ya en la revisión vigente, sin abrir una
            nueva, y queda en la auditoría.
          </Aviso>
        )}
        <Campo etiqueta="Manejo">
          <select
            value={manejo}
            onChange={(e) => setManejo(e.target.value as Manejo)}
            className={CLASE_ENTRADA}
          >
            {MANEJOS.map((m) => (
              <option key={m} value={m}>
                {NOMBRE_MANEJO[m]}
              </option>
            ))}
          </select>
        </Campo>
        <CamposControlRegistros
          codigo={codigo}
          nombre={nombre}
          puestoPorDefecto={actual.responsableAlmacenamiento}
          manejo={manejo}
          valor={valor}
          onCambio={setValor}
          errores={{}}
          sinBorrador={corregir}
        />
        {error && <Aviso>{error}</Aviso>}
        <div className="flex justify-end gap-2 pt-1">
          <Boton variante="secundario" onClick={onCerrar} disabled={guardar.isPending}>
            Cancelar
          </Boton>
          <Boton type="submit" disabled={guardar.isPending}>
            {guardar.isPending ? "Guardando…" : "Guardar"}
          </Boton>
        </div>
      </form>
    </Modal>
  );
}

/** El bloque "Control de registros" de la ficha del documento, con el botón para editarlo. */
export function ResumenDeControl({
  control,
  enCurso,
  puedeEditar,
  onEditar,
}: {
  control: Detalle["control"] & { registro: NonNullable<Detalle["control"]["registro"]> };
  /** La revisión en curso, si hay: el control propuesto rige al aprobarla. */
  enCurso: { numero: number } | null;
  puedeEditar: boolean;
  onEditar: () => void;
}) {
  return (
    <div className="mt-5 border-t border-borde pt-4">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[13px] font-bold text-marino">
          Control de registros
          {control.propuesto && (
            <Pildora tono="bg-ambar-fondo text-ambar">Cambio propuesto</Pildora>
          )}
        </div>
        {puedeEditar && (
          <button
            type="button"
            onClick={onEditar}
            className="text-[12px] font-semibold text-marino hover:underline"
          >
            Editar
          </button>
        )}
      </div>
      {control.propuesto && enCurso && (
        <div className="mb-2 text-[11.5px] text-texto-3">
          Así queda al aprobar la Rev. {formatearRevision(enCurso.numero)}; hasta entonces rige el
          control de la revisión vigente.
        </div>
      )}
      <div className="space-y-2">
        <Dato etiqueta="Copia digital">{control.ubicacionDigital}</Dato>
        <Dato etiqueta="Copia física">
          {control.registro.ubicacion ??
            (control.faltaUbicacionFisica ? (
              <span className="font-semibold text-rojo">Falta indicar dónde se archiva</span>
            ) : (
              <span className="text-texto-3">No tiene: es solo digital</span>
            ))}
        </Dato>
        <Dato etiqueta="Indexación">{control.registro.indexacion}</Dato>
        <Dato etiqueta="Responsable">{control.registro.responsableAlmacenamiento}</Dato>
        <Dato etiqueta="Retención">
          {control.registro.retencionMeses} meses ·{" "}
          {control.registro.disposicion === "destruccion" ? "Destrucción" : "Archivo pasivo"}
        </Dato>
      </div>
    </div>
  );
}
